import type { PremadeTemplate } from "@/lib/premade-contract-templates";

export const HOME_SERVICES_PROPOSAL_TEMPLATES = [
  "General Handyman Services",
  "Interior Painting Estimate",
  "Exterior Painting Estimate",
  "Bathroom Remodel Proposal",
  "Kitchen Renovation Proposal",
];

export const HOME_SERVICES_CONTRACT_TEMPLATES: PremadeTemplate[] = [
  {
    name: "Home Improvement Contract",
    description: "General home improvement agreement covering scope of work, materials, timeline, payment schedule, and warranty.",
    content: `HOME IMPROVEMENT CONTRACT

This Home Improvement Contract ("Contract") is entered into as of {{date}} by and between:

CONTRACTOR:
{{company_name}}
{{company_address}}
Phone: {{company_phone}} | Email: {{company_email}}
License #: ________________
(hereinafter referred to as "Contractor")

HOMEOWNER:
{{client_name}}
{{client_company}}
Email: {{client_email}} | Phone: {{client_phone}}
(hereinafter referred to as "Homeowner")

──────────────────────────────────────────────────

1. PROJECT DESCRIPTION

Property Address: {{project_address}}
Project Type: {{project_description}}

Contractor agrees to furnish all labor, materials, equipment, and supervision necessary to complete the home improvement project described herein.

──────────────────────────────────────────────────

2. SCOPE OF WORK

{{scope_of_work}}

The scope includes all work necessary to complete the project as described, including but not limited to:
• Demolition and removal of existing materials as needed
• Surface preparation and priming
• Installation of specified materials and finishes
• Fixture and hardware installation
• Final cleanup and debris removal
• Final walkthrough and punch list completion

Excluded from this contract:
• Work not explicitly described above
• Repairs to pre-existing damage discovered during construction
• Upgrades or changes requested after contract signing (change orders apply)

──────────────────────────────────────────────────

3. PROJECT TIMELINE

{{estimated_hours}}

• Estimated Start Date: ________________
• Estimated Completion Date: ________________
• Working Hours: 8:00 AM – 5:00 PM, Monday through Friday (unless otherwise agreed)

Delays caused by weather, material shortages, permit processing, or Homeowner-requested changes may extend the timeline. Contractor will notify Homeowner promptly of any delays.

──────────────────────────────────────────────────

4. COMPENSATION

Total Project Cost: \${{estimated_cost}}
Payment Terms: {{payment_terms}}

Payment Schedule:
• Deposit: ________% due upon contract signing
• Progress Payment: ________% due at project midpoint
• Final Payment: ________% due upon completion and final walkthrough

Late payments exceeding fifteen (15) days shall accrue interest at 1.5% per month. Contractor may suspend work if payment is more than thirty (30) days past due.

──────────────────────────────────────────────────

5. MATERIALS & SPECIFICATIONS

A. Contractor shall use materials of the quality and brand specified in the project scope.
B. Material substitutions require written approval from Homeowner.
C. Homeowner-supplied materials must be on-site before the scheduled installation date.
D. Unused materials remain Contractor's property unless purchased by Homeowner.

──────────────────────────────────────────────────

6. CHANGE ORDERS

Any changes to the scope, materials, or timeline must be documented in a written Change Order signed by both parties. Change Orders will specify:
• Description of the change
• Additional cost or credit
• Impact on project timeline

Work on changes will not begin until the Change Order is signed and any additional deposit is received.

──────────────────────────────────────────────────

7. PERMITS & INSPECTIONS

Contractor shall obtain all required building permits and schedule inspections as needed. Permit fees are:
☐ Included in the project cost
☐ Billed separately to Homeowner at cost

──────────────────────────────────────────────────

8. WARRANTY

Contractor warrants all workmanship for a period of ________ year(s) from completion date. This warranty covers:
• Defects in workmanship
• Installation failures under normal use
• Material defects (subject to manufacturer warranty)

This warranty does not cover:
• Normal wear and tear
• Damage from misuse, neglect, or acts of nature
• Modifications by parties other than Contractor

──────────────────────────────────────────────────

9. INSURANCE & LIABILITY

Contractor shall maintain:
• Commercial General Liability: $1,000,000 per occurrence / $2,000,000 aggregate
• Workers' Compensation: As required by state law
• Vehicle Insurance: As required by law

Contractor is responsible for damage to Homeowner's property caused by Contractor's negligence.

──────────────────────────────────────────────────

10. TERMINATION

A. Either party may terminate with written notice. Homeowner shall pay for all work completed and materials purchased through termination date.
B. Contractor may terminate if Homeowner fails to provide access or make timely payments.
C. If Homeowner terminates without cause, a cancellation fee of ________% of remaining contract value applies.

──────────────────────────────────────────────────

11. ADDITIONAL TERMS

{{terms_and_conditions}}

{{proposal_notes}}

──────────────────────────────────────────────────

12. SIGNATURES

CONTRACTOR:
Name: {{company_name}}
Signature: _________________________ Date: __________

HOMEOWNER:
Name: {{client_name}}
Signature: _________________________ Date: __________
`,
  },
  {
    name: "Painting Services Agreement",
    description: "Interior and exterior painting contract with surface prep, color specifications, and finish warranty.",
    content: `PAINTING SERVICES AGREEMENT

This Painting Services Agreement ("Agreement") is entered into as of {{date}} by and between:

PAINTING CONTRACTOR:
{{company_name}}
{{company_address}}
Phone: {{company_phone}} | Email: {{company_email}}
License #: ________________
(hereinafter referred to as "Contractor")

PROPERTY OWNER:
{{client_name}}
{{client_company}}
Email: {{client_email}} | Phone: {{client_phone}}
(hereinafter referred to as "Owner")

──────────────────────────────────────────────────

1. PROJECT DETAILS

Property Address: {{project_address}}
Project Type: {{project_description}}

──────────────────────────────────────────────────

2. SCOPE OF PAINTING SERVICES

{{scope_of_work}}

Standard painting services include:
• Surface inspection and condition assessment
• Patching, caulking, and surface preparation
• Priming of bare and repaired surfaces
• Application of specified coats of paint or stain
• Trim, door, and window frame painting as specified
• Daily cleanup of work areas
• Final inspection walkthrough

Surface preparation includes:
• Scraping loose or peeling paint
• Sanding rough surfaces
• Filling nail holes, cracks, and small imperfections
• Power washing exterior surfaces (exterior projects)

──────────────────────────────────────────────────

3. PAINT & COLOR SPECIFICATIONS

Brand: ________________
Finish/Sheen: ________________
Number of Coats: ________________

Color Schedule:
• ________________: Color ________________ (Code: ______________)
• ________________: Color ________________ (Code: ______________)
• ________________: Color ________________ (Code: ______________)

Color approval: Owner must approve all colors before application begins. Color changes after work starts are subject to Change Order fees.

──────────────────────────────────────────────────

4. PROJECT TIMELINE

{{estimated_hours}}

• Weather Delays (exterior): Work will be suspended during rain, high humidity (>85%), or temperatures below 50°F or above 95°F.

──────────────────────────────────────────────────

5. COMPENSATION

Total Cost: \${{estimated_cost}}
Payment Terms: {{payment_terms}}

──────────────────────────────────────────────────

6. OWNER RESPONSIBILITIES

• Move or protect furniture and personal items from work areas
• Provide access to all areas to be painted
• Approve colors and finishes before work begins
• Keep children and pets away from active work areas

──────────────────────────────────────────────────

7. WARRANTY

Contractor warrants paint application for ________ year(s) against peeling, blistering, or flaking caused by defective workmanship. Manufacturer warranty applies to paint products.

──────────────────────────────────────────────────

8. ADDITIONAL TERMS

{{terms_and_conditions}}

{{proposal_notes}}

──────────────────────────────────────────────────

9. SIGNATURES

CONTRACTOR:
Name: {{company_name}}
Signature: _________________________ Date: __________

OWNER:
Name: {{client_name}}
Signature: _________________________ Date: __________
`,
  },
  {
    name: "Remodeling Project Contract",
    description: "Full remodeling contract for kitchens, bathrooms, and living spaces with detailed phase breakdown and change order process.",
    content: `REMODELING PROJECT CONTRACT

This Remodeling Contract ("Contract") is entered into as of {{date}} by and between:

REMODELING CONTRACTOR:
{{company_name}}
{{company_address}}
Phone: {{company_phone}} | Email: {{company_email}}
License #: ________________
(hereinafter referred to as "Contractor")

HOMEOWNER:
{{client_name}}
{{client_company}}
Email: {{client_email}} | Phone: {{client_phone}}
(hereinafter referred to as "Homeowner")

──────────────────────────────────────────────────

1. PROJECT INFORMATION

Property Address: {{project_address}}
Remodel Type: {{project_description}}

──────────────────────────────────────────────────

2. DETAILED SCOPE OF WORK

{{scope_of_work}}

PHASE 1 – DEMOLITION & PREPARATION:
• Demolition of existing fixtures, cabinets, flooring, and surfaces as specified
• Removal and disposal of construction debris
• Protection of adjacent rooms and existing finishes

PHASE 2 – ROUGH WORK:
• Framing modifications as required
• Plumbing rough-in (by licensed plumber)
• Electrical rough-in (by licensed electrician)
• HVAC modifications as needed

PHASE 3 – INSTALLATION:
• Drywall installation, taping, and finishing
• Flooring installation
• Cabinet and countertop installation
• Tile and backsplash installation
• Fixture and appliance installation

PHASE 4 – FINISHING:
• Painting and trim work
• Hardware installation
• Final electrical and plumbing connections
• Comprehensive cleanup

PHASE 5 – COMPLETION:
• Final walkthrough with Homeowner
• Punch list items addressed within 14 days
• Certificate of completion

──────────────────────────────────────────────────

3. PROJECT TIMELINE

{{estimated_hours}}

Duration: {{contract_length}} {{contract_length_unit}}

──────────────────────────────────────────────────

4. COMPENSATION

Total Remodel Cost: \${{estimated_cost}}
Payment Terms: {{payment_terms}}

Payment Schedule:
• Contract Signing: ________% deposit
• Demolition Complete: ________%
• Rough Work Complete: ________%
• Installation Complete: ________%
• Final Completion: ________% (retained until punch list complete)

──────────────────────────────────────────────────

5. SUBCONTRACTORS

Contractor may engage licensed subcontractors for specialized work including plumbing, electrical, HVAC, and other trades. All subcontractors shall be properly licensed, insured, and supervised by Contractor.

──────────────────────────────────────────────────

6. PERMITS & CODES

Contractor shall:
• Obtain all required building permits
• Ensure all work meets local building codes
• Schedule and pass all required inspections
• Provide Homeowner with copies of all permits and inspection reports

──────────────────────────────────────────────────

7. CHANGE ORDERS

All changes must be documented in writing. Each Change Order shall include:
• Detailed description of the change
• Cost impact (additional charge or credit)
• Timeline impact
• Signatures of both parties

No change order work will begin without signed authorization and any required deposit.

──────────────────────────────────────────────────

8. WARRANTY

• Workmanship: ________ year(s) from completion
• Fixtures/Appliances: Manufacturer warranty applies
• Structural modifications: ________ year(s)

──────────────────────────────────────────────────

9. INSURANCE

Contractor maintains:
• General Liability: $1,000,000/$2,000,000
• Workers' Compensation: Statutory limits
• Builder's Risk: Project value

──────────────────────────────────────────────────

10. ADDITIONAL TERMS

{{terms_and_conditions}}

{{proposal_notes}}

──────────────────────────────────────────────────

11. SIGNATURES

CONTRACTOR:
Name: {{company_name}}
Signature: _________________________ Date: __________

HOMEOWNER:
Name: {{client_name}}
Signature: _________________________ Date: __________
`,
  },
  {
    name: "Recurring Maintenance Agreement",
    description: "Ongoing home maintenance service agreement for scheduled handyman visits with priority service and annual pricing.",
    content: `RECURRING HOME MAINTENANCE AGREEMENT

This Maintenance Agreement ("Agreement") is entered into as of {{date}} by and between:

SERVICE PROVIDER:
{{company_name}}
{{company_address}}
Phone: {{company_phone}} | Email: {{company_email}}
License #: ________________
(hereinafter referred to as "Provider")

HOMEOWNER:
{{client_name}}
{{client_company}}
Email: {{client_email}} | Phone: {{client_phone}}
(hereinafter referred to as "Homeowner")

──────────────────────────────────────────────────

1. SERVICE DESCRIPTION

Property Address: {{project_address}}
Service Plan: {{project_description}}

Provider agrees to furnish scheduled home maintenance services at the above property in accordance with this Agreement.

──────────────────────────────────────────────────

2. SCOPE OF MAINTENANCE SERVICES

{{scope_of_work}}

Standard maintenance services include:
• Seasonal home inspection and maintenance checklist
• Minor plumbing repairs (leaks, fixture adjustments, caulking)
• Minor electrical work (outlet covers, light fixtures, smoke detectors)
• Drywall patching and touch-up painting
• Door and window adjustments (hardware, weatherstripping)
• Caulking and sealing around tubs, showers, and windows
• Filter replacements (HVAC, range hood)
• Smoke and CO detector testing and battery replacement
• Gutter cleaning and downspout inspection
• General handyman tasks as time permits

Excluded services (available at additional cost):
• Major plumbing or electrical work requiring permits
• Roofing repairs
• Appliance installation or repair
• Structural modifications
• Projects requiring specialized licensing

──────────────────────────────────────────────────

3. SERVICE SCHEDULE

{{estimated_hours}}

Visit Frequency: ________________ (monthly / quarterly / semi-annual)
Hours Per Visit: ________________
Scheduling: Provider will contact Homeowner at least 48 hours in advance to schedule visits.

Priority Service: Homeowner receives priority scheduling for emergency maintenance requests with a guaranteed response within ________ business hours.

──────────────────────────────────────────────────

4. COMPENSATION

Service Fee: \${{estimated_cost}}
Payment Terms: {{payment_terms}}

• Billed monthly / quarterly (as agreed)
• Additional work beyond the scope of standard maintenance will be quoted separately and requires written approval
• Parts and materials used during maintenance visits are included up to $________ per visit; amounts exceeding this are billed at cost plus ________%

──────────────────────────────────────────────────

5. TERM & RENEWAL

Initial Term: {{contract_length}} {{contract_length_unit}}
Auto-Renewal: This Agreement automatically renews for successive terms of equal length unless either party provides thirty (30) days' written notice of cancellation prior to the renewal date.

──────────────────────────────────────────────────

6. INSURANCE & LIABILITY

Provider maintains:
• General Liability: $1,000,000 per occurrence
• Workers' Compensation: As required by law

Provider is not responsible for pre-existing conditions or damage not caused by Provider's work.

──────────────────────────────────────────────────

7. TERMINATION

Either party may terminate with thirty (30) days' written notice. Homeowner shall pay for all services rendered through the termination date.

──────────────────────────────────────────────────

8. ADDITIONAL TERMS

{{terms_and_conditions}}

{{proposal_notes}}

──────────────────────────────────────────────────

9. SIGNATURES

PROVIDER:
Name: {{company_name}}
Signature: _________________________ Date: __________

HOMEOWNER:
Name: {{client_name}}
Signature: _________________________ Date: __________
`,
  },
];
